import React, { useContext, useMemo } from "react";
import { ClientContext } from "../../App";

interface ClientItem {
  id: number | string;
  name_of_client: string;
  get_logo_url?: string;
}

const ClientGroup: React.FC = () => {
  const clientData = useContext(ClientContext);

  // Use only database items from ClientContext
  const marqueeList = useMemo(() => {
    if (!Array.isArray(clientData) || clientData.length === 0) {
      return [];
    }

    // Filter valid clients with either logo or name
    const validClients: ClientItem[] = clientData.filter(
      (item) => item && (item.get_logo_url || item.name_of_client)
    );

    if (validClients.length === 0) return [];

    // Ensure we have at least 14 items in the base array so the marquee loops seamlessly on wide screens
    let repeated = [...validClients];
    while (repeated.length < 14) {
      repeated = [...repeated, ...validClients];
    }

    // Double the array for seamless -50% translateX CSS marquee loop
    return [...repeated, ...repeated];
  }, [clientData]);

  if (marqueeList.length === 0) {
    return null;
  }

  return (
    <section className="cht-w-full cht-bg-white cht-py-12 sm:cht-py-16 cht-border-y cht-border-gray-100/80 cht-overflow-hidden">
      <div className="cht-container cht-mx-auto cht-px-4">
        {/* Header */}
        <div className="cht-text-center cht-mb-8 sm:cht-mb-10 cht-px-4">
          <p className="cht-text-[11px] sm:cht-text-xs cht-font-black cht-tracking-[0.25em] cht-uppercase cht-text-slate-400">
            Powering Experiences For The World's{" "}
            <span className="cht-text-[#6366f1] cht-font-black">Leading Brands</span>
          </p>
        </div>

        {/* Infinite Moving Marquee Ticker */}
        <div className="brand-marquee-container cht-relative cht-w-full cht-py-3">
          {/* Edge Fade Gradients for seamless visual transition */}
          <div className="cht-pointer-events-none cht-absolute cht-left-0 cht-top-0 cht-bottom-0 cht-w-20 sm:cht-w-36 cht-z-10 cht-bg-gradient-to-r cht-from-white cht-to-transparent" />
          <div className="cht-pointer-events-none cht-absolute cht-right-0 cht-top-0 cht-bottom-0 cht-w-20 sm:cht-w-36 cht-z-10 cht-bg-gradient-to-l cht-from-white cht-to-transparent" />

          {/* Marquee Track */}
          <div className="brand-marquee-track cht-flex cht-items-center cht-gap-14 sm:cht-gap-20">
            {marqueeList.map((client, idx) => (
              <div
                key={`${client.id}-${idx}`}
                className="brand-logo-item cht-h-14 cht-px-4 sm:cht-px-6 cht-flex cht-items-center cht-justify-center"
                title={client.name_of_client}
              >
                {client.get_logo_url ? (
                  <img
                    src={client.get_logo_url}
                    alt={client.name_of_client}
                    className="cht-max-h-9 sm:cht-max-h-11 cht-w-auto cht-max-w-[170px] cht-object-contain"
                    loading="lazy"
                    onError={(e) => {
                      // If image fails, fallback to rendering the text name
                      const target = e.target as HTMLElement;
                      target.style.display = "none";
                      if (target.nextElementSibling) {
                        (target.nextElementSibling as HTMLElement).style.display = "inline-block";
                      }
                    }}
                  />
                ) : null}
                <span
                  className={`cht-text-sm sm:cht-text-base cht-font-black cht-tracking-wider cht-uppercase cht-text-slate-800 ${
                    client.get_logo_url ? "cht-hidden" : "cht-inline-block"
                  }`}
                >
                  {client.name_of_client}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientGroup;
